/**
 * The bars drawn beside a voice message.
 *
 * These are not the real amplitudes. Measuring them would mean decoding the
 * whole recording on the sender's device, shipping the samples with the
 * message and storing them — for a decoration that is read at a glance and is
 * two hundred pixels wide. What matters is that the same message draws the
 * same shape everywhere, so a waveform is a property of the message rather
 * than of whoever happens to be looking at it: hence a hash of its id rather
 * than anything random.
 *
 * Heights come from the design handoff: 26 bars, 6–24px, 3px wide.
 */

/** How many bars a bubble draws — the handoff's count. */
export const WAVEFORM_BARS = 26;

const MIN_HEIGHT = 6;
const MAX_HEIGHT = 24;

/**
 * A message id is a UUID, and a waveform needs a number. Any cheap, stable
 * spread will do; this is the string hash every codebase ends up with.
 */
function seedOf(id: string): number {
  let hash = 0;
  for (let index = 0; index < id.length; index += 1) {
    hash = (hash * 31 + id.charCodeAt(index)) | 0;
  }
  return Math.abs(hash % 1000);
}

/**
 * Bar heights in pixels, deterministic in `seed`. The two constants are the
 * prototype's: they are what make consecutive bars differ enough to read as a
 * waveform rather than a comb.
 */
export function waveformBars(seed: number, count = WAVEFORM_BARS): number[] {
  const bars: number[] = [];
  for (let index = 0; index < count; index += 1) {
    const value = Math.abs(Math.sin(seed * 0.73 + index * 1.31));
    bars.push(MIN_HEIGHT + Math.round(value * (MAX_HEIGHT - MIN_HEIGHT)));
  }
  return bars;
}

/** The same, keyed by message id — what the bubbles actually call. */
export function waveformFor(messageId: string, count = WAVEFORM_BARS): number[] {
  return waveformBars(seedOf(messageId), count);
}
